import { useMemo, useRef } from 'react';
import { throttle, ThrottleSettings } from 'lodash';
import { QueryKey, useQueryClient } from 'react-query';

/**
 * Returns a throttled function that invalidates the given query keys at most once per
 * interval, regardless of how often it is called. Useful for heavy queries refreshed in
 * response to high-volume triggers (SignalR events, button mashing).
 */
export function useThrottledInvalidateQueries(
  queryKeys: QueryKey[],
  intervalMs: number,
  options: ThrottleSettings = { leading: true, trailing: true }
) {
  const queryClient = useQueryClient();

  // keep the latest keys available to the throttled callback without resetting the
  // throttle window when the array identity changes
  const queryKeysRef = useRef(queryKeys);
  queryKeysRef.current = queryKeys;

  const { leading, trailing } = options;

  return useMemo(
    () =>
      throttle(
        () => {
          queryKeysRef.current.forEach((key) => queryClient.invalidateQueries(key));
        },
        intervalMs,
        { leading, trailing }
      ),
    [queryClient, intervalMs, leading, trailing]
  );
}
